import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Dialog, Box, Typography, IconButton, CircularProgress, Chip, Tooltip, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import RoomIcon from '@material-ui/icons/Room';
import PersonIcon from '@material-ui/icons/Person';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DescriptionIcon from '@material-ui/icons/Description';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { jsPDF } from 'jspdf';
import { AuthContext } from '../../App';

// Diálogo "Relatório de Entrega" — vê o relatório que o motorista
// submeteu via app, incluindo foto da carga/local + assinatura do
// cliente. Aplica-se a OTs com status='delivered' (e a OTs com
// outcome partial / followup_needed / customer_absent / cancelled
// que também submetem relatório). Os dados vêm da tabela
// assistance_reports e estão incluídos na resposta de /transport-orders.

const useStyles = makeStyles(() => ({
  paper: {
    // Altura mais contida — 78vh em vez de 92vh, para não ocupar o
    // ecrã todo e o operador ver o resto da página por trás.
    width: '92vw', maxWidth: 880, maxHeight: '78vh',
    borderRadius: 16, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    borderBottom: '1px solid #6ee7b7',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  headerIcon: {
    width: 40, height: 40, borderRadius: 12,
    background: '#16a34a', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 10px rgba(22,163,74,0.3)',
  },
  body: {
    flex: 1, minHeight: 0, overflowY: 'auto', padding: 0,
    // Barra de scroll discreta mas visível — ajuda o operador a
    // perceber que pode rolar para ver mais conteúdo.
    '&::-webkit-scrollbar': { width: 10 },
    '&::-webkit-scrollbar-track': { background: 'rgba(15,23,42,0.04)' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(99,102,241,0.35)', borderRadius: 6,
      '&:hover': { background: 'rgba(99,102,241,0.55)' },
    },
  },
  section: {
    padding: '14px 20px',
    borderBottom: '1px solid #f1f5f9',
  },
  sectionTitle: {
    fontSize: '0.62rem', fontWeight: 800,
    color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
  },
  metaCell: {
    padding: '8px 12px',
    borderRadius: 8,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
  },
  metaLabel: {
    fontSize: '0.58rem', fontWeight: 700,
    color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em',
    marginBottom: 2,
  },
  metaValue: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
  },
  outcomeBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 999,
    fontSize: '0.74rem', fontWeight: 800,
  },
  textBlock: {
    fontSize: '0.84rem', color: '#1e293b', lineHeight: 1.55,
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    padding: '10px 14px',
    background: '#f8fafc',
    borderLeft: '3px solid #6366f1',
    borderRadius: '0 8px 8px 0',
  },
  signaturePlaceholder: {
    background: '#fafbfc',
    border: '2px dashed #e2e8f0',
    borderRadius: 12,
    padding: '30px 16px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '0.78rem',
  },
  signatureCard: {
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
    padding: 14,
    boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
  },
  signatureImg: {
    width: '100%', maxHeight: 280, objectFit: 'contain',
    display: 'block',
    background: 'linear-gradient(180deg, #fafbfc 0%, #fff 100%)',
    borderRadius: 8, border: '1px solid #f1f5f9',
    padding: 8, boxSizing: 'border-box',
  },
  photosGrid: {
    display: 'grid',
    // Quadrículas maiores (200px em vez de 140) — operador vê o que
    // a foto mostra sem ter de a ampliar.
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  photoBox: {
    position: 'relative',
    paddingBottom: '75%',
    borderRadius: 10, overflow: 'hidden',
    cursor: 'zoom-in',
    background: '#f1f5f9',
    boxShadow: '0 2px 6px rgba(15,23,42,0.06)',
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
    },
  },
  photoImg: {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%',
    objectFit: 'cover',
  },
  photoOverlay: {
    position: 'absolute', top: 6, right: 6,
    background: 'rgba(15,23,42,0.7)', color: '#fff',
    width: 26, height: 26, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'none',
  },
  ratingBox: {
    display: 'inline-flex', alignItems: 'center', gap: 2,
  },
  // Lightbox for photo zoom — z-index acima da Dialog do MUI (que usa
  // 1300 por defeito) para que ao clicar numa foto a versão grande
  // apareça por cima do popup do relatório, não por trás.
  lightbox: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(0,0,0,0.94)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 40,
    cursor: 'zoom-out',
  },
  lightboxImg: {
    maxWidth: 'calc(100vw - 80px)', maxHeight: 'calc(100vh - 80px)',
    objectFit: 'contain', cursor: 'default',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
    borderRadius: 6,
    background: '#fff',
  },
  lightboxClose: {
    position: 'absolute', top: 16, right: 20,
    color: '#fff', cursor: 'pointer', fontSize: 32,
    width: 44, height: 44, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.1)',
    transition: 'background 0.15s',
    '&:hover': { background: 'rgba(255,255,255,0.25)' },
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #e2e8f0',
    background: '#fafbfc',
    display: 'flex', gap: 10,
    justifyContent: 'flex-end', alignItems: 'center',
  },
  pdfBtn: {
    background: 'linear-gradient(135deg, #16a34a, #15803d)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    padding: '7px 18px',
    boxShadow: '0 2px 6px rgba(22,163,74,0.30)',
    '&:hover': { background: 'linear-gradient(135deg, #15803d, #166534)' },
    '&:disabled': { background: '#cbd5e1', color: '#fff' },
  },
}));

// Outcome → label/colour mapping. Mirrors mobile/src/screens/ReportScreen.js.
const OUTCOMES = {
  resolved: { label: 'Serviço resolvido', color: '#16a34a', bg: '#dcfce7', emoji: '✓' },
  partial: { label: 'Resolvido parcialmente', color: '#f59e0b', bg: '#fef3c7', emoji: '◐' },
  followup_needed: { label: 'Necessita nova visita', color: '#3b82f6', bg: '#dbeafe', emoji: '↻' },
  customer_absent: { label: 'Cliente ausente', color: '#94a3b8', bg: '#f1f5f9', emoji: '⊘' },
  cancelled: { label: 'Serviço cancelado', color: '#dc2626', bg: '#fee2e2', emoji: '✗' },
};

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(min) {
  if (min == null || min < 0) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

// Normalise photo references. The mobile app submits objects of the
// shape `{ id, dataUrl }` (dataUrl é uma string completa "data:image/...").
// Aceita também strings base64, paths server-relative e URLs http(s)
// para máxima compatibilidade com versões anteriores e futuras.
function normalisePhotos(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map(p => {
    if (!p) return null;
    if (typeof p === 'string') {
      if (p.startsWith('data:') || p.startsWith('http')) return p;
      if (p.startsWith('/')) return p;
      return `data:image/jpeg;base64,${p}`;
    }
    if (typeof p === 'object') {
      // Ordem de preferência: dataUrl (formato actual da app), url
      // (HTTP), path (server-relative), data (raw base64).
      return p.dataUrl || p.url || p.path
        || (p.data ? `data:image/jpeg;base64,${p.data}` : null);
    }
    return null;
  }).filter(Boolean);
}

export default function OrderReportDialog({ open, onClose, orderId }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoomPhoto, setZoomPhoto] = useState(null);

  useEffect(() => {
    if (!open || !orderId || !token) {
      setOrder(null);
      setZoomPhoto(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/planner-api/transport-orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled) setOrder(data); })
      .catch(() => { if (!cancelled) setOrder(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, orderId, token]);

  if (!open) return null;

  const report = order?.assistanceReport || null;
  const orderRef = (order?.customRef || '').trim() || order?.orderNumber || `OT-${orderId}`;
  const photos = normalisePhotos(report?.photos);
  const signature = report?.customerSignature || null;
  const driverSignature = report?.driverSignature || null;
  const outcome = OUTCOMES[report?.outcome] || OUTCOMES.resolved;
  const rating = report?.customerRating ? Math.max(0, Math.min(5, Number(report.customerRating))) : 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }} maxWidth={false}>
        <Box className={classes.header}>
          <Box className={classes.headerIcon}>
            <DescriptionIcon style={{ fontSize: 22 }} />
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography style={{ fontSize: '0.96rem', fontWeight: 800, color: '#064e3b' }}>
              Relatório de entrega
            </Typography>
            <Typography style={{ fontSize: '0.74rem', color: '#047857', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {orderRef}{order?.deliveryName ? ` · ${order.deliveryName}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.body}>
          {loading && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && !report && order && (
            <Box style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <Typography style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: 6 }}>
                Sem relatório
              </Typography>
              <Typography style={{ fontSize: '0.74rem' }}>
                Esta ordem ainda não tem relatório submetido pelo motorista na app.
              </Typography>
            </Box>
          )}

          {!loading && report && (
            <>
              {/* Meta grid: outcome + dates + duration + customer */}
              <Box className={classes.section}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span className={classes.outcomeBadge} style={{ background: outcome.bg, color: outcome.color }}>
                    <span style={{ fontSize: 14 }}>{outcome.emoji}</span> {outcome.label}
                  </span>
                  {rating > 0 && (
                    <Box className={classes.ratingBox}>
                      {[1, 2, 3, 4, 5].map(i => (
                        i <= rating
                          ? <StarIcon key={i} style={{ fontSize: 17, color: '#f59e0b' }} />
                          : <StarBorderIcon key={i} style={{ fontSize: 17, color: '#cbd5e1' }} />
                      ))}
                      <Typography style={{ fontSize: '0.66rem', color: '#64748b', marginLeft: 4, fontWeight: 600 }}>
                        {rating}/5
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box className={classes.metaGrid}>
                  {/* Chegada com badge da origem + tooltip — operador vê
                      imediatamente se foi marcada pelo motorista
                      ('Cheguei'), auto-detectada (geofence/GPS history)
                      ou aproximada (route-start-fallback). Hover revela
                      a explicação completa de como o valor foi obtido. */}
                  {(() => {
                    const arrival = report.arrivedAt || order.actualStartAt;
                    if (!arrival) return null;
                    const sourceCfg = ({
                      manual:                 { label: 'Marcada pelo motorista', color: '#16a34a',
                        tip: 'O motorista carregou no botão "Cheguei" no app quando entrou no local · valor REAL e mais fiável.' },
                      geofence:               { label: 'Detecção automática (geofence)', color: '#16a34a',
                        tip: 'O app detectou automaticamente a chegada quando o GPS entrou num raio de 150m do local · valor REAL.' },
                      'gps-history':          { label: 'Calculada do histórico GPS', color: '#0ea5e9',
                        tip: 'O motorista não carregou em "Cheguei" e o geofence não disparou (provavelmente sinal GPS instável), mas o histórico GPS guardado tem um fix dentro de 100m do local · valor QUASE REAL recuperado retroactivamente.' },
                      'route-start-fallback': { label: 'Aproximada · início da rota', color: '#f59e0b',
                        tip: '⚠ Não foi possível detectar a chegada real (motorista não carregou em "Cheguei", geofence falhou e o histórico GPS não tem fix junto ao local). Como último recurso, é usado o momento em que a rota arrancou · APROXIMAÇÃO POBRE para rotas com várias paragens.' },
                      body:                   { label: 'Submetida pela app', color: '#16a34a',
                        tip: 'Hora de chegada enviada explicitamente pela app no momento de submissão do relatório · valor REAL.' },
                      existing:               { label: 'Marcada pelo motorista', color: '#16a34a',
                        tip: 'Hora gravada anteriormente pelo botão "Cheguei" e preservada no momento da submissão do relatório · valor REAL.' },
                    })[report.arrivedSource] || null;
                    return (
                      <Tooltip
                        title={sourceCfg
                          ? <span style={{ whiteSpace: 'pre-line', fontSize: '0.74rem', lineHeight: 1.5 }}>{sourceCfg.tip}</span>
                          : 'Origem do registo de chegada não identificada.'}
                        placement="top"
                        arrow
                        enterDelay={200}
                      >
                        <Box className={classes.metaCell} style={{ cursor: 'help' }}>
                          <Typography className={classes.metaLabel}>
                            <AccessTimeIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />
                            Chegada
                          </Typography>
                          <Typography className={classes.metaValue}>
                            {formatDateTime(arrival)}
                          </Typography>
                          {sourceCfg && (
                            <Typography style={{
                              fontSize: '0.56rem', fontWeight: 700, marginTop: 3,
                              color: sourceCfg.color, letterSpacing: '0.02em',
                            }}>
                              • {sourceCfg.label}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  <Box className={classes.metaCell}>
                    <Typography className={classes.metaLabel}>
                      <AccessTimeIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />
                      Conclusão
                    </Typography>
                    <Typography className={classes.metaValue}>
                      {formatDateTime(report.completedAt || order.deliveredAt)}
                    </Typography>
                  </Box>
                  {/* Duração: prioriza report.actualDurationMin, cai para
                      cálculo a partir de actualStartAt → deliveredAt.
                      Esconde quando não dá para calcular. A precisão da
                      duração depende da precisão da chegada — refletida
                      no tooltip para o operador entender. */}
                  {(() => {
                    let dur = report.actualDurationMin;
                    if (dur == null && order.actualStartAt && (order.deliveredAt || report.completedAt)) {
                      const start = new Date(order.actualStartAt).getTime();
                      const end = new Date(order.deliveredAt || report.completedAt).getTime();
                      dur = Math.max(0, Math.round((end - start) / 60000));
                    }
                    if (dur == null) return null;
                    // Tooltip explica como foi obtida — ligando à
                    // origem da chegada quando aplicável.
                    const arrivedTooltip = ({
                      manual:                 'Tempo entre o "Cheguei" do motorista e a submissão do relatório · valor REAL.',
                      geofence:               'Tempo entre detecção automática de chegada (geofence 150m) e a submissão do relatório · valor REAL.',
                      'gps-history':          'Tempo entre o primeiro fix GPS dentro de 100m do local e a submissão do relatório · valor QUASE REAL (recuperado do histórico).',
                      'route-start-fallback': '⚠ Tempo entre o INÍCIO DA ROTA e a submissão do relatório · APROXIMADO. Para rotas multi-stop sub-estima a duração desta OT específica. Pode reflectir o tempo total da rota até este ponto, não só o serviço.',
                      body:                   'Duração enviada explicitamente pela app do motorista.',
                      existing:               'Tempo entre o "Cheguei" do motorista e a submissão do relatório · valor REAL.',
                    })[report.arrivedSource] || 'Duração calculada do tempo entre chegada e conclusão.';
                    const sourceCfg = ({
                      manual:                 { label: 'do registo do motorista', color: '#16a34a' },
                      geofence:               { label: 'da detecção automática', color: '#16a34a' },
                      'gps-history':          { label: 'do histórico GPS', color: '#0ea5e9' },
                      'route-start-fallback': { label: 'aproximada · ver tooltip', color: '#f59e0b' },
                      body:                   { label: 'submetida pela app', color: '#16a34a' },
                      existing:               { label: 'do registo do motorista', color: '#16a34a' },
                    })[report.arrivedSource] || null;
                    return (
                      <Tooltip
                        title={<span style={{ whiteSpace: 'pre-line', fontSize: '0.74rem', lineHeight: 1.5 }}>{arrivedTooltip}</span>}
                        placement="top"
                        arrow
                        enterDelay={200}
                      >
                        <Box className={classes.metaCell} style={{ cursor: 'help' }}>
                          <Typography className={classes.metaLabel}>
                            Duração
                          </Typography>
                          <Typography className={classes.metaValue}>
                            {formatDuration(dur)}
                          </Typography>
                          {sourceCfg && (
                            <Typography style={{
                              fontSize: '0.56rem', fontWeight: 700, marginTop: 3,
                              color: sourceCfg.color, letterSpacing: '0.02em',
                            }}>
                              • {sourceCfg.label}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })()}
                  {report.customerName && (
                    <Box className={classes.metaCell}>
                      <Typography className={classes.metaLabel}>
                        <PersonIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />
                        Recebido por
                      </Typography>
                      <Typography className={classes.metaValue} style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {report.customerName}
                      </Typography>
                    </Box>
                  )}
                  {(order.deliveryName || order.deliveryAddress) && (
                    <Box className={classes.metaCell} style={{ gridColumn: 'span 2' }}>
                      <Typography className={classes.metaLabel}>
                        <RoomIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />
                        Local
                      </Typography>
                      <Typography className={classes.metaValue} style={{ fontSize: '0.78rem', whiteSpace: 'normal' }}>
                        {order.deliveryName}
                        {order.deliveryAddress && order.deliveryAddress !== order.deliveryName && (
                          <Typography component="span" style={{ display: 'block', fontSize: '0.7rem', fontWeight: 500, color: '#475569' }}>
                            {order.deliveryAddress}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Work performed */}
              {report.workPerformed && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    🛠 Trabalho realizado
                  </Typography>
                  <Box className={classes.textBlock}>{report.workPerformed}</Box>
                </Box>
              )}

              {/* Materials used */}
              {Array.isArray(report.materialsUsed) && report.materialsUsed.length > 0 && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    📦 Materiais aplicados ({report.materialsUsed.length})
                  </Typography>
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {report.materialsUsed.map((m, i) => {
                      const label = typeof m === 'string'
                        ? m
                        : `${m.name || m.description || ''}${m.quantity ? ` × ${m.quantity}` : ''}`;
                      return (
                        <Chip
                          key={i}
                          label={label}
                          size="small"
                          style={{
                            background: '#eef2ff',
                            color: '#4338ca',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Follow-up notes */}
              {report.followUpNotes && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle} style={{ color: '#dc2626' }}>
                    ↻ Necessita acompanhamento
                  </Typography>
                  <Box className={classes.textBlock} style={{ borderLeftColor: '#dc2626', background: '#fef2f2' }}>
                    {report.followUpNotes}
                  </Box>
                </Box>
              )}

              {/* General notes */}
              {report.notes && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    📝 Observações
                  </Typography>
                  <Box className={classes.textBlock}>{report.notes}</Box>
                </Box>
              )}

              {/* Photos */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  📷 Fotografias{photos.length > 0 ? ` (${photos.length})` : ''}
                </Typography>
                {photos.length === 0 ? (
                  <Box className={classes.signaturePlaceholder}>
                    Sem fotografias submetidas
                  </Box>
                ) : (
                  <Box className={classes.photosGrid}>
                    {photos.map((src, i) => (
                      <Tooltip key={i} title="Clique para ampliar">
                        <Box
                          className={classes.photoBox}
                          onClick={() => setZoomPhoto(src)}
                        >
                          <img src={src} alt={`Foto ${i + 1}`} className={classes.photoImg} />
                          <Box className={classes.photoOverlay}>
                            <FullscreenIcon style={{ fontSize: 14 }} />
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Email ao cliente — preparação para quando o SMTP estiver
                  ligado. Mostra o destinatário (delivery_email da OT ou
                  o que o motorista preencheu) e o estado: "Enviado em ..."
                  com timestamp, ou "Não enviado". */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  ✉ Email ao cliente
                </Typography>
                <Box style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  display: 'grid', gridTemplateColumns: '120px 1fr', columnGap: 14, rowGap: 4,
                  fontSize: '0.78rem',
                }}>
                  <Typography style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Destinatário</Typography>
                  <Typography style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 600 }}>
                    {order?.deliveryEmail || '—'}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Estado</Typography>
                  {report.emailSentAt ? (
                    <Typography style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 700 }}>
                      Enviado em {formatDateTime(report.emailSentAt)}
                      {report.emailSentTo && report.emailSentTo !== order?.deliveryEmail && (
                        <span style={{ color: '#64748b', fontWeight: 500 }}> · para {report.emailSentTo}</span>
                      )}
                    </Typography>
                  ) : (
                    <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      Não enviado
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Signatures — duas: cliente e motorista. Lado a lado quando
                  ambas existem; senão só a presente. Mantém o pattern
                  original do dialog para o cliente e espelha-o para o
                  motorista. */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  ✍ Assinaturas
                </Typography>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Box style={{ flex: '1 1 240px', minWidth: 220 }}>
                    <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                      Cliente
                    </Typography>
                    {!signature ? (
                      <Box className={classes.signaturePlaceholder}>
                        Sem assinatura submetida
                      </Box>
                    ) : (
                      <Box className={classes.signatureCard}>
                        <img
                          src={signature.startsWith('data:') ? signature : `data:image/png;base64,${signature}`}
                          alt="Assinatura do cliente"
                          className={classes.signatureImg}
                        />
                        {report.customerName && (
                          <Typography style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569', marginTop: 8, textAlign: 'center' }}>
                            — {report.customerName} —
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box style={{ flex: '1 1 240px', minWidth: 220 }}>
                    <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                      Motorista
                    </Typography>
                    {!driverSignature ? (
                      <Box className={classes.signaturePlaceholder}>
                        Sem assinatura submetida
                      </Box>
                    ) : (
                      <Box className={classes.signatureCard}>
                        <img
                          src={driverSignature.startsWith('data:') ? driverSignature : `data:image/png;base64,${driverSignature}`}
                          alt="Assinatura do motorista"
                          className={classes.signatureImg}
                        />
                        {order?.driverName && (
                          <Typography style={{ fontSize: '0.74rem', fontWeight: 600, color: '#475569', marginTop: 8, textAlign: 'center' }}>
                            — {order.driverName} —
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Footer com acção "Gerar PDF" — só visível quando há relatório
            submetido pelo motorista (caso contrário não há nada para
            gerar). O PDF é montado client-side com jsPDF, incluindo
            fotos e assinatura embebidas em base64. */}
        {!loading && report && (
          <Box className={classes.footer}>
            <Button
              className={classes.pdfBtn}
              startIcon={<PictureAsPdfIcon style={{ fontSize: 18 }} />}
              onClick={() => generateReportPdf(order, report, orderRef, outcome, rating, photos, signature, driverSignature)}
            >
              Gerar PDF
            </Button>
          </Box>
        )}
      </Dialog>

      {/* Photo lightbox — fullscreen view, portalled to body para ficar
          acima do Dialog (que está num portal próprio do MUI). Sem isto
          o lightbox aparecia por trás do popup e o operador via apenas
          o ecrã preto sem a imagem. */}
      {zoomPhoto && ReactDOM.createPortal(
        <Box className={classes.lightbox} onClick={() => setZoomPhoto(null)}>
          <span className={classes.lightboxClose} onClick={() => setZoomPhoto(null)}>✕</span>
          <img src={zoomPhoto} alt="" className={classes.lightboxImg} onClick={(e) => e.stopPropagation()} />
        </Box>,
        document.body
      )}
    </>
  );
}

// Gera um PDF profissional do relatório de entrega/assistência.
// Inclui cabeçalho, OT info, datas, motorista/veículo, descrição do
// serviço, materiais, fotos embebidas e assinatura. A4 portrait, com
// quebra de página automática quando o conteúdo passa o limite.
function generateReportPdf(order, report, orderRef, outcome, rating, photos, signature, driverSignature) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const PAGE_W = 210;
  const PAGE_H = 297;
  const MARGIN = 15;
  const CONTENT_W = PAGE_W - 2 * MARGIN;
  let y = MARGIN;

  function ensureSpace(needed) {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  }

  function sectionTitle(label) {
    ensureSpace(12);
    doc.setFillColor(241, 245, 249);
    doc.rect(MARGIN, y - 1, CONTENT_W, 7, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label.toUpperCase(), MARGIN + 2, y + 4);
    y += 10;
  }

  function kvRow(k, v) {
    if (v == null || v === '') return;
    ensureSpace(6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`${k}:`, MARGIN + 2, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
    const text = doc.splitTextToSize(String(v), CONTENT_W - 32);
    doc.text(text, MARGIN + 30, y);
    y += Math.max(5, text.length * 4.5);
  }

  function multilineBlock(text, opts = {}) {
    if (!text) return;
    const fontSize = opts.fontSize || 9;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(30, 41, 59);
    const lines = doc.splitTextToSize(String(text), CONTENT_W - 4);
    ensureSpace(lines.length * 4.5 + 4);
    doc.text(lines, MARGIN + 2, y);
    y += lines.length * 4.5 + 3;
  }

  // ─── Cabeçalho ───────────────────────────────────────────────
  doc.setFillColor(22, 163, 74);
  doc.rect(0, 0, PAGE_W, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Relatório de Entrega', MARGIN, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`${orderRef}${order?.deliveryName ? ' · ' + order.deliveryName : ''}`, MARGIN, 22);
  doc.setFontSize(8);
  doc.text(`Gerado em ${new Date().toLocaleString('pt-PT')}`, PAGE_W - MARGIN, 22, { align: 'right' });
  y = 36;
  doc.setTextColor(15, 23, 42);

  // ─── Resultado + classificação ────────────────────────────────
  sectionTitle('Resultado do serviço');
  const outcomeText = `${outcome.emoji}  ${outcome.label}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(outcome.color));
  doc.text(outcomeText, MARGIN + 2, y);
  if (rating > 0) {
    const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating);
    doc.setTextColor(245, 158, 11);
    doc.text(`${stars}  (${rating}/5)`, MARGIN + 90, y);
  }
  y += 8;

  // ─── Tempos + duração ────────────────────────────────────────
  sectionTitle('Tempos');
  kvRow('Chegada', formatDateTime(report.arrivedAt));
  kvRow('Conclusão', formatDateTime(report.completedAt));
  if (report.arrivedAt && report.completedAt) {
    const durMin = Math.round((new Date(report.completedAt) - new Date(report.arrivedAt)) / 60000);
    kvRow('Duração total', formatDuration(durMin));
  }
  y += 2;

  // ─── Veículo / Motorista ─────────────────────────────────────
  if (order?.vehiclePlate || order?.driverName || report.driverId) {
    sectionTitle('Veículo & Motorista');
    kvRow('Matrícula', order?.vehiclePlate);
    kvRow('Motorista', order?.driverName);
    y += 2;
  }

  // ─── Email ao cliente ────────────────────────────────────────
  // Mostra o destinatário (delivery_email da OT) e o estado de envio.
  // Não enviado fica em itálico; enviado mostra timestamp e, se for
  // diferente do destinatário guardado, também a morada usada.
  if (order?.deliveryEmail || report.emailSentAt) {
    sectionTitle('Email ao cliente');
    kvRow('Destinatário', order?.deliveryEmail || '—');
    if (report.emailSentAt) {
      const sentLabel = report.emailSentTo && report.emailSentTo !== order?.deliveryEmail
        ? `Enviado em ${formatDateTime(report.emailSentAt)} para ${report.emailSentTo}`
        : `Enviado em ${formatDateTime(report.emailSentAt)}`;
      kvRow('Estado', sentLabel);
    } else {
      kvRow('Estado', 'Não enviado');
    }
    y += 2;
  }

  // ─── Locais ───────────────────────────────────────────────────
  if (order?.pickupName || order?.pickupAddress || order?.deliveryName || order?.deliveryAddress) {
    sectionTitle('Locais');
    if (order.pickupName || order.pickupAddress) {
      kvRow('Recolha', order.pickupName ? `${order.pickupName} — ${order.pickupAddress || ''}` : order.pickupAddress);
    }
    if (order.deliveryName || order.deliveryAddress) {
      kvRow('Entrega', order.deliveryName ? `${order.deliveryName} — ${order.deliveryAddress || ''}` : order.deliveryAddress);
    }
    y += 2;
  }

  // ─── Cliente ──────────────────────────────────────────────────
  if (report.customerName || order?.deliveryContact) {
    sectionTitle('Cliente');
    kvRow('Recebido por', report.customerName || order?.deliveryContact);
    kvRow('Contacto', order?.deliveryPhone);
    y += 2;
  }

  // ─── Descrição do serviço ─────────────────────────────────────
  if (report.notes || report.serviceDescription) {
    sectionTitle('Descrição do serviço');
    multilineBlock(report.serviceDescription || report.notes);
  }

  // ─── Materiais usados (assistência técnica) ──────────────────
  if (Array.isArray(report.materials) && report.materials.length > 0) {
    sectionTitle('Materiais utilizados');
    report.materials.forEach(m => {
      const qty = m.quantity || m.qty || 1;
      const label = m.description || m.name || m.item || '—';
      kvRow(`${qty}×`, label);
    });
    y += 2;
  }

  // ─── Fotografias ──────────────────────────────────────────────
  if (Array.isArray(photos) && photos.length > 0) {
    sectionTitle(`Fotografias (${photos.length})`);
    const PHOTO_W = (CONTENT_W - 4) / 2;
    const PHOTO_H = 55;
    let col = 0;
    photos.forEach((src) => {
      ensureSpace(PHOTO_H + 6);
      const x = MARGIN + col * (PHOTO_W + 4);
      try {
        // Determina o formato da imagem para o jsPDF (JPEG ou PNG).
        const fmt = /^data:image\/png/i.test(src) ? 'PNG' : 'JPEG';
        doc.addImage(src, fmt, x, y, PHOTO_W, PHOTO_H, undefined, 'FAST');
      } catch (e) {
        // Falha silenciosa por foto — não compromete o PDF.
      }
      col = (col + 1) % 2;
      if (col === 0) y += PHOTO_H + 4;
    });
    if (col !== 0) y += PHOTO_H + 4;
  }

  // ─── Assinaturas (cliente + motorista) ───────────────────────
  // Quando ambas existem, desenha lado a lado. Quando só uma, centra.
  if (signature || driverSignature) {
    ensureSpace(70);
    sectionTitle('Assinaturas');
    const SIG_W = 70, SIG_H = 32;
    const both = signature && driverSignature;
    const drawSig = (src, x, label) => {
      const sigSrc = src.startsWith('data:') ? src : `data:image/png;base64,${src}`;
      try {
        doc.addImage(sigSrc, 'PNG', x, y, SIG_W, SIG_H, undefined, 'FAST');
      } catch (e) {/* skip */}
      if (label) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(`— ${label} —`, x + SIG_W / 2, y + SIG_H + 5, { align: 'center' });
      }
    };
    if (both) {
      const gap = 10;
      const startX = (PAGE_W - (SIG_W * 2 + gap)) / 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('CLIENTE',   startX + SIG_W / 2, y - 1, { align: 'center' });
      doc.text('MOTORISTA', startX + SIG_W + gap + SIG_W / 2, y - 1, { align: 'center' });
      drawSig(signature,        startX, report.customerName || null);
      drawSig(driverSignature,  startX + SIG_W + gap, order?.driverName || null);
      y += SIG_H + 12;
    } else if (signature) {
      const x = (PAGE_W - SIG_W) / 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('CLIENTE', x + SIG_W / 2, y - 1, { align: 'center' });
      drawSig(signature, x, report.customerName || null);
      y += SIG_H + 9;
    } else if (driverSignature) {
      const x = (PAGE_W - SIG_W) / 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('MOTORISTA', x + SIG_W / 2, y - 1, { align: 'center' });
      drawSig(driverSignature, x, order?.driverName || null);
      y += SIG_H + 9;
    }
  }

  // ─── Rodapé em cada página ───────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `EcooGPS Planner · ${orderRef} · Página ${i} de ${pageCount}`,
      PAGE_W / 2, PAGE_H - 6,
      { align: 'center' }
    );
  }

  doc.save(`relatorio-${orderRef}.pdf`);
}

// Converte hex "#16a34a" → [22,163,74] para passar ao jsPDF setTextColor.
function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return [15, 23, 42];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
